import { useState } from 'react'
import { router } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloudIcon from '@mui/icons-material/Cloud'
import SettingsIcon from '@mui/icons-material/Settings'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import type { DeploymentPackage } from '@/types'

const DEPLOYMENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  docker_compose: <ViewInArIcon fontSize="small" />,
  okonomi_paas: <CloudIcon fontSize="small" />,
  helm: <SettingsIcon fontSize="small" />,
}

interface Props {
  vendor: { id: number; name: string; slug: string }
  app_entry: { id: number; name: string; slug: string }
  release: { id: number; version: string; status: string; editable: boolean }
  packages: DeploymentPackage[]
}

export default function DeploymentPackagesIndex({ vendor, app_entry, release, packages }: Props) {
  const [deleteTarget, setDeleteTarget] = useState<DeploymentPackage | null>(null)
  const basePath = `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${release.id}`

  const handleDelete = () => {
    if (!deleteTarget) return
    router.delete(`${basePath}/deployment_packages/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton
          onClick={() => router.visit(`/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases`)}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            Deployment Packages — v{release.version}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {vendor.name} / {app_entry.name}
          </Typography>
        </Box>
        {release.editable && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => router.visit(`${basePath}/deployment_packages/new`)}
          >
            New Package
          </Button>
        )}
      </Box>

      {packages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No deployment packages yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add deployment packages to define how this release can be deployed.
          </Typography>
          {release.editable && (
            <Button
              variant="contained"
              onClick={() => router.visit(`${basePath}/deployment_packages/new`)}
            >
              Add First Package
            </Button>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>Config</TableCell>
                {release.editable && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {DEPLOYMENT_TYPE_ICONS[pkg.deploymentType]}
                      <Typography variant="body2">{pkg.deploymentTypeLabel}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight={600}>
                      {pkg.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {pkg.variantLabel || '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {pkg.hasSchema && <Chip label="Schema" size="small" color="info" />}
                      {pkg.hasTemplate && <Chip label="Template" size="small" color="success" />}
                      {(pkg.fileTemplatesCount ?? 0) > 0 && (
                        <Chip label={`${pkg.fileTemplatesCount} files`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  {release.editable && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => router.visit(`${basePath}/deployment_packages/${pkg.id}/edit`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(pkg)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Package</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the package{' '}
            <strong>{deleteTarget?.name}</strong>? This will also remove all associated
            file templates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  )
}
